
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { OpsEndpointCard } from '@/components/ops/OpsEndpointCard';
import { TimestampDisplay } from '@/components/ops/TimestampDisplay';
import { EmptyState } from '@/components/ops/EmptyState';
import { LoadingSpinner } from '@/components/ops/LoadingSpinner';
import { OpsErrorBoundary } from '@/components/ops/OpsErrorBoundary';
import { Search, X, ShieldCheck } from 'lucide-react';

const API_BASE = import.meta.env.VITE_RAG_API_URL;

interface AdaptiveEvent {
    id: string;
    tenant_id: string;
    request_id: string | null;
    event_type: string;
    intent: string | null;
    policy: string;
    semantic_before: number | null;
    semantic_after: number | null;
    created_at: string;
}


export function AdaptiveAuditPage() {
    const { session } = useUser();
    const [events, setEvents] = useState<AdaptiveEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tenantFilter, setTenantFilter] = useState('');
    const [limit, setLimit] = useState(50);
    const isMounted = useRef(true);

    const fetchData = async () => {
        if (!session?.access_token) return;

        try {
            setLoading(true);
            setError(null);

            const params: any = { limit };
            if (tenantFilter.trim()) {
                params.tenant_id = tenantFilter.trim();
            }

            const res = await axios.get(`${API_BASE}/ops/retrieval/adaptive/events`, {
                params,
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 5000
            });

            if (isMounted.current) {
                setEvents(res.data.events || []);
            }
        } catch (err: any) {
            if (isMounted.current) {
                if (err.response?.status === 401) {
                    setError("Unauthorized");
                } else {
                    setError(err.message || "Failed to fetch adaptive events");
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        isMounted.current = true;
        if (session?.access_token) {
            fetchData();
        } else {
            setLoading(false);
            setError("Authentication required");
        }
        return () => {
            isMounted.current = false;
        };
    }, [session]);  // Only re-fetch on session change initially, other fetches manual or triggered

    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchData();
    };

    const clearFilter = () => {
        setTenantFilter('');
        // We'll let the user click "Filter" again or refresh to clear results, 
        // to avoid auto-triggering on clear which might be annoying if they just want to type a new one.
        // Actually, clearing usually implies "show all", so triggering fetch makes sense.
        // But state update is async. Let's just set state and then finding a way to trigger fetch effectively would require useEffect dependency 
        // or a separate ref. For simplicity, let's keep it manual or user has to click filter button.
        // Better UX: clear and trigger.
        setTenantFilter('');
        setTimeout(fetchData, 0);
    };

    if (!session) return <EmptyState title="Authentication Required" description="Please sign in to view adaptive audit log." />;

    return (
        <OpsErrorBoundary>
            <div className="h-[calc(100vh-12rem)] max-w-6xl mx-auto p-4">
                <OpsEndpointCard
                    title="Adaptive Runtime Audit"
                    endpoint="/ops/retrieval/adaptive/events"
                    lastUpdated={Date.now()}
                    onRefresh={fetchData}
                    isLoading={loading}
                    error={error}
                >
                    <div className="space-y-4">
                        {/* Filter Controls */}
                        <form onSubmit={handleFilterSubmit} className="flex gap-4 items-end pb-4 border-b border-white/10">
                            <div className="flex-1 max-w-md space-y-2">
                                <label className="text-sm text-gray-400">Filter by Tenant ID</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={tenantFilter}
                                        onChange={(e) => setTenantFilter(e.target.value)}
                                        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none pr-8"
                                        placeholder="UUID..."
                                    />
                                    {tenantFilter && (
                                        <button
                                            type="button"
                                            onClick={clearFilter}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="w-24 space-y-2">
                                <label className="text-sm text-gray-400">Limit</label>
                                <select
                                    value={limit}
                                    onChange={(e) => setLimit(Number(e.target.value))}
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                >
                                    <option value={10}>10</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 mb-[1px]"
                            >
                                <Search className="w-4 h-4" />
                                Add Filter
                            </button>
                        </form>

                        {/* Events Table */}
                        {loading && events.length === 0 ? (
                            <LoadingSpinner fullHeight text="Loading adaptive events..." />
                        ) : events.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-gray-400 uppercase bg-black/40 border-b border-white/10">
                                        <tr>
                                            <th className="px-4 py-3">Event Type</th>
                                            <th className="px-4 py-3">Intent</th>
                                            <th className="px-4 py-3">Policy</th>
                                            <th className="px-4 py-3">Tenant</th>
                                            <th className="px-4 py-3 text-right">Created At</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {events.map((event) => (
                                            <tr key={event.id} className="hover:bg-white/5 transition-colors">
                                                <td className="px-4 py-3 font-medium text-white">{event.event_type}</td>
                                                <td className="px-4 py-3 text-gray-300">{event.intent || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-xs border ${event.policy === 'strict' ? 'border-red-900 text-red-400 bg-red-900/10' :
                                                        event.policy === 'restrictive' ? 'border-orange-900 text-orange-400 bg-orange-900/10' :
                                                            'border-blue-900 text-blue-400 bg-blue-900/10'
                                                        }`}>
                                                        {event.policy}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-gray-500">{event.tenant_id.slice(0, 8)}...</td>
                                                <td className="px-4 py-3 text-right">
                                                    <TimestampDisplay timestamp={event.created_at} showIcon={false} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12">
                                <EmptyState
                                    icon={ShieldCheck}
                                    title="No Events Found"
                                    description={tenantFilter ? "No events match this tenant filter." : "No adaptive events have been recorded yet."}
                                />
                            </div>
                        )}

                        {/* Footer / Count */}
                        {events.length > 0 && (
                            <div className="text-right text-xs text-gray-500 pt-2 border-t border-white/5">
                                Showing {events.length} most recent events
                            </div>
                        )}
                    </div>
                </OpsEndpointCard>
            </div>
        </OpsErrorBoundary>
    );
}
